import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { initializeIcons } from '@fluentui/react';
import { isFeatureEnabled, FeatureFlags } from './config/featureFlags';

import './index.css';

import Layout from './pages/layout/Layout';
import NoPage from './pages/NoPage';
import InterviewQuestions from './pages/interviewquestions/InterviewQuestions';
import CandidateReport from './pages/candidatereport/CandidateReport';
import ClientProposal from './pages/clientproposal/ClientProposal';
import TrafficLightReport from './pages/trafficlightreport/TrafficLightReport';
import ConsultantBios from './pages/masterlists/ConsultantBios';
import JobRoles from './pages/masterlists/JobRoles';
import CoverLetter from './pages/coverletter/CoverLetter';
import PromptAdjuster from './pages/masterlists/PromptAdjuster';
import { AuthProvider, AuthProviderProps } from './auth/AuthProvider';
import { PublicClientApplication } from '@azure/msal-browser/dist/app/PublicClientApplication';
import { appConfig } from './appConfig';
import { IntlProvider } from './i18n/IntlProvider';
import { AuthErrorProvider } from './components/AuthErrorProvider';
import { webLightTheme } from '@fluentui/react-components';
import { FluentProvider } from '@fluentui/react-components';
import { useIsAuthenticated, useAccount } from '@azure/msal-react';

initializeIcons();

const authConfig: AuthProviderProps = {
  clientId: appConfig.msal.clientId,
  authority: appConfig.msal.authority,
  scopes: [`api://${appConfig.msal.clientId}/shk`],
  redirectUri: '/',
  postLogoutRedirectUri: '/logout',
  apiBaseUrl: location.origin,
  publicClientApplication: {} as PublicClientApplication,
};

const ProtectedRoute = ({
  element,
  adminOnly = false,
  featureFlag,
}: {
  element: React.ReactNode;
  adminOnly?: boolean;
  featureFlag?: keyof FeatureFlags;
}) => {
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();

  const roles = account?.idTokenClaims?.roles || [];
  const isAdmin = roles.includes('Task.Write');

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/candidatereport" />;
  }

  if (featureFlag && !isFeatureEnabled(featureFlag)) {
    return <Navigate to="/candidatereport" />;
  }

  return element;
};

export default function App() {
  return (
    <FluentProvider theme={webLightTheme}>
      <AuthProvider {...authConfig}>
        <IntlProvider>
          <BrowserRouter>
            <AuthErrorProvider>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<Navigate to="/candidatereport" />} />
                  <Route path="candidatereport" element={<CandidateReport />} />
                  <Route path="trafficlightreport" element={<TrafficLightReport />} />
                  <Route
                    path="coverletter"
                    element={<ProtectedRoute adminOnly featureFlag="coverLetter" element={<CoverLetter />} />}
                  />
                  <Route path="clientproposal" element={<ProtectedRoute adminOnly element={<ClientProposal />} />} />
                  <Route
                    path="interviewquestions"
                    element={<ProtectedRoute adminOnly element={<InterviewQuestions />} />}
                  />
                  <Route
                    path="/admin/consultantbios"
                    element={<ProtectedRoute adminOnly element={<ConsultantBios />} />}
                  />
                  <Route path="/admin/jobroles" element={<ProtectedRoute adminOnly element={<JobRoles />} />} />
                  <Route
                    path="/admin/promptadjuster"
                    element={<ProtectedRoute adminOnly element={<PromptAdjuster />} />}
                  />
                  <Route path="*" element={<NoPage />} />
                </Route>
              </Routes>
            </AuthErrorProvider>
          </BrowserRouter>
        </IntlProvider>
      </AuthProvider>
    </FluentProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
